/**
 * Password-recovery state.
 *
 * Plain words: this module answers one question, "is the person using this browser
 * part-way through a password reset?", and it reads the reset link out of the URL.
 *
 * It is a LEAF MODULE: it imports nothing from this project, on purpose.
 * `@/integrations/supabase/client` calls `createClient` at module scope using
 * `import.meta.env.VITE_SUPABASE_URL`. There is no `.env` under Vitest, so anything that
 * reaches it throws `supabaseUrl is required` at import time and takes the whole test file
 * down with it. Keeping this module import-free means the guard, the reset page and their
 * tests can all use it without a database client. The half that needs the auth client
 * (`initRecoveryDetection`) lives in `recovery-init.ts` for the same reason.
 *
 * Requirements: FR-005 (the state survives closing the browser), FR-009 (the URL half),
 * FR-013 (the state is scoped to one user and lapses after an hour; D-045 reads that user
 * from the link itself).
 */

/** The one reset path, exported so the form, the route and the guard cannot drift apart. */
export const RESET_PATH = "/reset-password";

/** Where a finished or irrelevant reset sends the user. D-039 gave "/" to the public landing page. */
export const APP_HOME = "/app";

export const RECOVERY_KEY = "budgeai.password-recovery";

/**
 * The URL fragment as it looked when the app started.
 *
 * Read once, synchronously, at module evaluation: supabase-js sets
 * `window.location.hash = ''` as soon as it has exchanged the tokens, so reading the hash
 * later inside an effect is a race against that clear. `main.tsx` imports this module above
 * the App import so the snapshot happens first.
 */
export const BOOT_HASH: string = typeof window !== "undefined" ? window.location.hash : "";

export interface RecoveryLinkError {
  /** Supabase's `error_code`, e.g. `otp_expired`. The verdict, never the wording (D-044). */
  code: string;
}

export interface RecoveryRecord {
  userId: string | null;
  startedAt: number;
}

/** One hour, matching the lifetime of a Supabase recovery link (FR-013). */
export const RECOVERY_TTL_MS = 60 * 60 * 1000;

/**
 * In-memory fallback for a browser that refuses storage (private mode, blocked site data).
 * Kept in step with localStorage so a reset still works for the life of the page even when
 * nothing can be written down.
 */
let memoryRecord: RecoveryRecord | null = null;

const subscribers = new Set<(active: boolean) => void>();

function params(hash: string): URLSearchParams {
  return new URLSearchParams(hash.replace(/^#/, ""));
}

/** A fragment value as the Supabase client reads it: of a repeated key, the last one wins. */
function last(q: URLSearchParams, key: string): string | null {
  const all = q.getAll(key);
  return all.length > 0 ? all[all.length - 1] : null;
}

/** Does the fragment report a failed link? The same test the Supabase client applies. */
function reportsLinkError(q: URLSearchParams): boolean {
  return !!(last(q, "error") || last(q, "error_code") || last(q, "error_description"));
}

/** The `sub` claim of a JWT, read without checking the signature; null if there is none. */
function subjectOf(token: string): string | null {
  const payload = token.split(".")[1];
  if (!payload) return null;
  try {
    const claims: unknown = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    const sub = typeof claims === "object" && claims !== null ? (claims as { sub?: unknown }).sub : null;
    return typeof sub === "string" && sub !== "" ? sub : null;
  } catch {
    return null;
  }
}

/**
 * Whose recovery link is this? The user id it was issued for, or null when the fragment is
 * not a live recovery link: it must say `type=recovery`, report no error, and carry an access
 * token that names a user.
 *
 * Supabase's access token is a JWT whose `sub` claim is the user it was issued for. The
 * signature is not checked, and does not need to be: the answer only decides WHOSE reset this
 * is, while signing in is still done by the Supabase client with the same token. Reading the
 * owner here, rather than waiting for the client to report a session, keeps the reset on the
 * right person in the two cases where the client's report is wrong or missing (D-045):
 *
 *  - The client does not always act on the link. A value in the query string overrides the
 *    fragment (`?access_token=` blanks the token), and a few error codes make it keep whoever
 *    was already signed in. The session found afterwards is then somebody else's.
 *  - The PASSWORD_RECOVERY event fires once, and the app can start listening after it.
 */
export function recoveryUserFromUrl(hash: string): string | null {
  if (!hash) return null;
  const q = params(hash);
  if (last(q, "type") !== "recovery" || reportsLinkError(q)) return null;
  return subjectOf(last(q, "access_token") ?? "");
}

/**
 * The error Supabase puts in the fragment for an expired, used or malformed link, e.g.
 * `#error=access_denied&error_code=otp_expired&error_description=Email+link+is+invalid+or+has+expired`.
 *
 * This is the only trustworthy verdict on a dead link. It must never be inferred from a
 * missing session: someone already signed in who clicks a dead link would then be shown a
 * working password form and would change the password of the account on that browser
 * (FR-009, SC-006).
 */
export function detectRecoveryErrorFromUrl(hash: string): RecoveryLinkError | null {
  if (!hash) return null;
  const q = params(hash);
  // Any one of `error`, `error_code` or `error_description` means the link failed, as it does
  // to the Supabase client. Checking `error` alone let a fragment with only an `error_code`
  // through, and for some codes the client then keeps an older session signed in (D-044).
  if (!reportsLinkError(q)) return null;
  // The description is deliberately not read. Nothing signs the fragment: anyone can build
  // this URL and send it, so its prose is theirs, not Supabase's. Shown as the page's own copy
  // it would put words in the app's mouth, under our heading, on our domain ("call this
  // number to verify your account"). React escapes markup, so this is not script injection;
  // it is the more useful half of a phishing attack. The page says what a dead link means in
  // its own words instead (D-044).
  return { code: last(q, "error_code") || last(q, "error") || "unknown" };
}

function writeRecord(record: RecoveryRecord | null): void {
  memoryRecord = record;
  try {
    if (record === null) {
      window.localStorage.removeItem(RECOVERY_KEY);
    } else {
      window.localStorage.setItem(RECOVERY_KEY, JSON.stringify(record));
    }
  } catch {
    // Storage denied. The in-memory copy above carries the state for this page.
  }
}

function readRecord(): RecoveryRecord | null {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(RECOVERY_KEY);
  } catch {
    return memoryRecord;
  }
  if (raw === null) return memoryRecord;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // A corrupt record is not a reset in progress. Drop it rather than trapping the browser.
    writeRecord(null);
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) {
    writeRecord(null);
    return null;
  }
  const candidate = parsed as Partial<RecoveryRecord>;
  if (typeof candidate.startedAt !== "number") {
    writeRecord(null);
    return null;
  }
  return {
    userId: typeof candidate.userId === "string" ? candidate.userId : null,
    startedAt: candidate.startedAt,
  };
}

function notify(active: boolean): void {
  for (const cb of subscribers) cb(active);
}

/**
 * Is this browser mid-reset?
 *
 * Pass the signed-in user's id when it is known. A reset belonging to someone else is
 * ignored, so a shared browser does not march the next person who signs in to "choose a new
 * password" for an account whose password they already know (FR-013). When the caller does
 * not know who is signed in, the state still applies: silently unlocking would re-open the
 * hole this feature closes.
 */
export function isInRecovery(currentUserId?: string | null): boolean {
  const record = readRecord();
  if (!record) return false;

  if (Date.now() - record.startedAt > RECOVERY_TTL_MS) {
    // Lapsed. A forgotten reset must not lock a browser out for ever.
    writeRecord(null);
    return false;
  }

  if (record.userId && currentUserId && record.userId !== currentUserId) return false;

  return true;
}

export function beginRecovery(userId?: string | null): void {
  writeRecord({ userId: userId ?? null, startedAt: Date.now() });
  notify(true);
}

export function endRecovery(): void {
  writeRecord(null);
  notify(false);
}

/** Subscribe to begin/end so the guard can react without polling. Returns the unsubscribe. */
export function subscribeRecovery(cb: (active: boolean) => void): () => void {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}
